import React from 'react';

import { FavoriteFieldChip } from 'shared/components/internal/FavoriteFieldChip';
import { FIELDS_PREFIX } from 'shared/components/internal/FavoriteFields';

import { RecordColumnValueType, RecordType } from '../schema';
import { getPropertyLabel } from '../schema/v3/RecordOverview/RecordProperty';

const DEVICE_IP_NAME = 'device_ip';
const DST_DEVICE_IP_NAME = 'dstDevice_ip';
const SRC_DEVICE_IP_NAME = 'srcDevice_ip';
export const METADATA_PREFIX = 'metadata_';

export function useVisibleColumnData(
  allFavoriteFields: string[],
  allColumns: RecordColumnValueType[],
  record: RecordType
): [visibleColumns: RecordColumnValueType[], visibleFavoriteField: string[]] {
  const existingFields = getExistingFields(allColumns, record);

  const reducedColumns = allColumns.slice(0, allColumns.length - 1);

  const visibleFavoriteFields = getVisibleFavoriteFields(
    allFavoriteFields,
    existingFields,
    reducedColumns
  );

  const visibleColumns = visibleFavoriteFields.length
    ? // If we are displaying a favorite field, then we want to replace the last column
      // with the favorite field renderer
      reducedColumns.concat({
        renderer: getFavoriteFieldsRenderer(visibleFavoriteFields, record),
      })
    : allColumns;

  return [visibleColumns, visibleFavoriteFields];
}

export function getVisibleFavoriteFields(
  allFavoriteFields: string[],
  existingFields: string[],
  reducedColumns: RecordColumnValueType[]
): string[] {
  const allFavoriteFieldsExist = allFavoriteFields.every((field) =>
    existingFields.includes(field)
  );

  // We don't want to display any favorite fields if they're all captured by
  // existing fields.
  if (allFavoriteFieldsExist) {
    return [];
  }

  const visibleRecordColumnFields = getRecordColumnFields(reducedColumns);
  const visibleExistingFields = existingFields.filter((field) =>
    visibleRecordColumnFields.has(field)
  );

  // Remove only displayed fields from favorite fields. If the final column can be favorited,
  // we still want to display it in the record's favorite fields.
  return allFavoriteFields.filter(
    (field) => !visibleExistingFields.includes(field)
  );
}

function getExistingFields(
  columns: RecordColumnValueType[],
  record: RecordType
): string[] {
  const existingFields = getRecordColumnFields(columns);

  // Edge case: The backend may duplicate the Device IP Address and the
  // Source Device IP Address in the schema. Since we display the source and
  // destination IP addresses in the header already, do not display the
  // device IP address in the favorite fields if the source or destination
  // IP address fields already exist in the header.
  if (
    record.hasOwnProperty(DEVICE_IP_NAME) &&
    !existingFields.has(DEVICE_IP_NAME)
  ) {
    if (
      existingFields.has(SRC_DEVICE_IP_NAME) &&
      record[SRC_DEVICE_IP_NAME] === record[DEVICE_IP_NAME]
    ) {
      existingFields.add(DEVICE_IP_NAME);
    } else if (
      existingFields.has(DST_DEVICE_IP_NAME) &&
      record[DST_DEVICE_IP_NAME] === record[DEVICE_IP_NAME]
    ) {
      existingFields.add(DEVICE_IP_NAME);
    }
  }

  return [...existingFields];
}

function getFavoriteFieldsRenderer(
  visibleFavoriteFields: string[],
  record: RecordType
) {
  return () => (
    <div>
      {visibleFavoriteFields.map((name) => (
        <FavoriteFieldChip
          key={`fav-field-chip-${name}`}
          prefix={getPropertyLabel(
            name,
            undefined,
            name.startsWith(FIELDS_PREFIX)
          )}
          name={name}
          record={record}
        />
      ))}
    </div>
  );
}

function getRecordColumnFields(columns: RecordColumnValueType[]): Set<string> {
  const fields = new Set<string>(
    columns.flatMap(({ primary, secondary }) => [
      primary ?? '',
      secondary ?? '',
    ])
  );

  fields.delete('');

  return fields;
}
